import React, { useState, useCallback, useEffect } from 'react';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    LinearProgress,
    TextField,
} from '@material-ui/core';
import Box from '@material-ui/core/Box';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { OrderDto, updateOrderSourceAddress, updateOrderTargetAddress } from '../../../api/orders';
import { OrderAddressTypeEnum } from './OrderAddressEnum';
import { AddressDto, fetchAddresses } from 'api/services-post';
import { debounce } from 'lodash';

interface Props {
    onChange: (value: boolean) => void;
    onClose: () => void;
    addressType: OrderAddressTypeEnum;
    open: boolean;
    order: OrderDto;
    onSuccess: () => void;
}

export const OrderAdressEditDialog = ({ onSuccess, order, addressType, onClose, open, onChange }: Props) => {
    const defaultAddress = addressType === OrderAddressTypeEnum.SOURCE ? order?.order_address?.source.address : order?.order_address?.target.address;
    const [suggestions, setSuggestions] = useState<string[]>([]);
    const [fieldValue, setFieldValue] = useState<string>('');
    const [startValue, setStartValue] = useState<boolean>(false);

    useEffect(() => {
        if (open) {
            setStartValue(true);
            setFieldValue(defaultAddress);
        }
    }, [defaultAddress, addressType]);

    const fetchSuggestions = async (input: string): Promise<string[]> => {
        const addresses: AddressDto[] = await fetchAddresses(input);
        const _suggestions: string[] = Array.from(new Set(addresses.map((address) => address.addressRus))).slice(0, 10);
        return _suggestions;
    };

    const debounceSetSuggestions = useCallback(debounce(async (value: string) => {
        const query: string = (order?.region?.description ? order.region.description + ' ' : '') + value;
        const fetchedSuggestions = await fetchSuggestions(query);
        setSuggestions(fetchedSuggestions);
    }, 300), []);

    const handleInputChange = (value: string) => {
        if(startValue){
            setStartValue(false);
            return
        }
        setFieldValue(value);

        if (value.length >= 3) {
            debounceSetSuggestions(value);
        } else {
            setSuggestions([]);
        }
    };

    return (
        <Dialog open={open} fullWidth={true} onClose={onClose}>
            <Formik
                enableReinitialize={true}
                initialValues={addressType === OrderAddressTypeEnum.SOURCE ? order?.order_address?.source : order?.order_address?.target}
                validationSchema={Yup.object().shape({
                    address: Yup.string()
                        .required('Адрес не может быть пустым')
                        .min(3, 'Адрес должен быть не менее 3 символов'),
                })}
                onSubmit={async (values, { setSubmitting }) => {

                    if (addressType === OrderAddressTypeEnum.TARGET) {
                        await updateOrderTargetAddress({ order_id: order.id, address: values.address })
                    } else {
                        await updateOrderSourceAddress({ order_id: order.id, address: values.address })
                    }

                    onChange(true);
                    setSubmitting(false);
                    onSuccess();
                    onClose()
                }}
            >
                {({ isSubmitting, setFieldValue }) => (
                    <Form>
                        <DialogTitle>
                            {addressType === OrderAddressTypeEnum.TARGET && 'Адрес доставки'}
                            {addressType === OrderAddressTypeEnum.SOURCE && 'Адрес отправки'}
                        </DialogTitle>
                        <DialogContent>
                            <Box display="flex" flexDirection="column">
                                <Autocomplete
                                    freeSolo
                                    options={suggestions}
                                    inputValue={fieldValue}
                                    filterOptions={(options) => options}
                                    onInputChange={(_, newInputValue) => {
                                        setFieldValue('address', newInputValue);
                                        handleInputChange(newInputValue);
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Введите адрес"
                                            variant="outlined"
                                            fullWidth
                                            value={fieldValue}
                                        />
                                    )}
                                />
                            </Box>
                            {isSubmitting && <LinearProgress />}
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={onClose}>Отмена</Button>
                            <Button type="submit" disabled={isSubmitting}>Ок</Button>
                        </DialogActions>
                    </Form>
                )}
            </Formik>
        </Dialog>
    );
};

export default OrderAdressEditDialog;
